import {useMemo} from 'react';
import {useParams} from 'react-router';
import {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {DateRangeOnChange} from '../../../date-range-input/date-range-utils';
import {ScreenPaths} from '../../../../enums/screen-path';
import {useSeniorSidebar} from '../../../../contexts/seniors-sidebar/context';
import {useAuth} from '../../../../contexts/auth/context';
import {CareServicesHeader} from '../../base/header/care-services-header';
import {
  CareServicePreparedExportData,
  CareServiceCreatingItem,
  OnCareServiceExportPdfClick,
} from '../../base/data/care-service-item';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';

interface SeniorCareServicesHeaderProps {
  startDate: Moment;
  endDate: Moment;
  canWrite?: boolean;
  filterUsers: FetchEntityUsersResult;
  onDateChange: DateRangeOnChange;
  onCreateItemClick: (newItem: CareServiceCreatingItem) => void;
  onFilterUserChange: (user: AffiliatedUser | undefined) => void;
  onExportPdfClick: OnCareServiceExportPdfClick;
  onExportXlsxClick?: (preparedData: CareServicePreparedExportData) => void;
}

export function SeniorCareServicesHeader(props: SeniorCareServicesHeaderProps) {
  const {url, entityId, id}: {url: string; entityId: string; id: string} = useParams();
  const seniorSidebarCtx = useSeniorSidebar();
  const authContext = useAuth();
  const {t} = useTranslation();
  const senior = seniorSidebarCtx.state?.senior?.originalData;
  const seniorName = senior?.displayName || senior?.name;
  const entity = authContext.state.session?.selectedAffiliation?.entity;

  const crumbs = useMemo(() => {
    return [
      {
        label: t(translations.screen_paths.professional_caregiver_seniors),
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
      },
      {
        label: seniorName,
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
      },
      {
        label: t(translations.seniorSideMenu.careServices),
        link: url,
      },
    ];
  }, [seniorName, entityId, id, url, t]);

  return (
    <CareServicesHeader
      isLoading={senior === undefined}
      crumbs={crumbs}
      startDate={props.startDate}
      endDate={props.endDate}
      canWrite={props.canWrite}
      onDateChange={props.onDateChange}
      onCreateItemClick={(item) => {
        // --- additional fields ---
        // add targetUserId as the current senior id
        item.targetUserId = id;

        props.onCreateItemClick(item);
      }}
      filterUsers={props.filterUsers}
      filterDefaultOptionLabel={translations.care_services.header.filter_caregivers_label}
      onFilterUserChange={props.onFilterUserChange}
      onExportPdfClick={async () => {
        props.onExportPdfClick({
          startDate: props.startDate,
          endDate: props.endDate,
          user: senior,
          entity,
        });
      }}
      onExportXlsxClick={
        props.onExportXlsxClick
          ? () => {
              props.onExportXlsxClick?.({user: senior, entity});
            }
          : undefined
      }
    />
  );
}
